import { useRef, useState } from 'react';
import type { DemStandardSalesLine } from '../../lib/demStandardSalesLines';

type StandardSalesLinesPanelProps = {
  lines: DemStandardSalesLine[];
  loading: boolean;
  customerCode: string;
  onHide: () => void;
};

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

export function StandardSalesLinesPanel({
  lines,
  loading,
  customerCode,
  onHide,
}: StandardSalesLinesPanelProps) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = async (code: string) => {
    const ok = await copyText(code);
    if (!ok) return;

    setCopiedCode(code);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopiedCode(null), 1600);
  };

  return (
    <aside className="dem-po-review__std" aria-label="Standard sales lines">
      <div className="dem-po-review__std-toolbar">
        <h3 className="dem-po-review__section-title">Standard Sales Lines</h3>
        <button
          type="button"
          className="dem-po-review__std-hide"
          onClick={onHide}
          title="Hide standard sales lines"
          aria-label="Hide standard sales lines"
          aria-expanded
        >
          ×
        </button>
      </div>
      <p className="dem-po-review__std-desc">
        Frequent items for this customer. Select or double-click a row to copy the item code.
      </p>

      {copiedCode ? (
        <p className="dem-po-review__std-copied" role="status">
          Copied <strong>{copiedCode}</strong>
        </p>
      ) : null}

      {!customerCode ? (
        <p className="dem-po-review__std-empty">Select a customer code to load frequent items.</p>
      ) : loading ? (
        <p className="dem-po-review__std-empty" role="status">
          Loading…
        </p>
      ) : lines.length === 0 ? (
        <p className="dem-po-review__std-empty">No standard sales lines for this customer.</p>
      ) : (
        <div className="dem-po-review__std-table-wrap">
          <div className="dem-po-review__std-head" aria-hidden>
            <span>Code</span>
            <span>Description</span>
          </div>
          {lines.map((line) => (
            <button
              key={line.item_code}
              type="button"
              className={`dem-po-review__std-row${line.unknown ? ' dem-po-review__std-row--unknown' : ''}${
                copiedCode === line.item_code ? ' dem-po-review__std-row--copied' : ''
              }`}
              title={`Copy ${line.item_code}`}
              onClick={() => void handleCopy(line.item_code)}
              onDoubleClick={() => void handleCopy(line.item_code)}
            >
              <span className="dem-po-review__std-code">{line.item_code}</span>
              <span className="dem-po-review__std-desc-cell">{line.description}</span>
            </button>
          ))}
        </div>
      )}
    </aside>
  );
}
